import { useMemo, useCallback, MutableRefObject } from 'react';
import { FieldInputProps, FieldMetaState } from 'react-final-form';
import * as S from '../Select.styled';

interface SelectInputProps<OptionValue> {
    ref?: React.MutableRefObject<HTMLInputElement>;
    input?: FieldInputProps<OptionValue | OptionValue[], HTMLElement>;
    searchValue: string;
    setSearchValue: (value: string) => void;
    isOpen: boolean;
    setIsOpen: (value: boolean) => void;
    placeholder: string;
    fillPlaceholder: boolean;
    label?: string;
    meta?: FieldMetaState<OptionValue | OptionValue[]>;
    search?: boolean;
    required?: boolean;
    value?: OptionValue | OptionValue[];
    rightContent?: React.ReactNode;
    shouldClose: MutableRefObject<boolean>;
    keepSearch?: boolean;
    hideMeta?: boolean;
}

export function SelectInput<OptionValue>({
    ref,
    input,
    searchValue,
    setSearchValue,
    isOpen,
    setIsOpen,
    placeholder,
    fillPlaceholder,
    label,
    meta,
    search = true,
    required,
    value,
    rightContent,
    shouldClose,
    hideMeta
}: SelectInputProps<OptionValue>) {
    const inputProps = useMemo<FieldInputProps<string, HTMLElement>>(
        () => ({
            value: searchValue,
            name: input?.name || Symbol().toString(),
            onFocus: (event) => {
                if (shouldClose.current) {
                    event?.target?.blur();
                } else {
                    input?.onFocus?.(event);
                }
            },
            onBlur:
                input?.onBlur ??
                (() => {
                    return;
                }),
            onChange: (event) => {
                setIsOpen(true);
                setSearchValue(event.target.value);
            }
        }),
        [input, searchValue, setSearchValue, setIsOpen, shouldClose]
    );

    const handleClick = useCallback(() => {
        if (shouldClose.current) {
            shouldClose.current = false;
            setIsOpen(false);
        } else {
            setIsOpen(!isOpen);
        }
    }, [setIsOpen, isOpen, shouldClose]);

    return (
        <S.Input
            ref={ref}
            input={inputProps}
            onClick={handleClick}
            placeholder={placeholder}
            meta={meta ?? {}}
            label={label}
            $isOpen={isOpen}
            $fillPlaceholder={fillPlaceholder}
            readOnly={!search}
            required={
                required &&
                (!value || (Array.isArray(value) && value.length === 0))
            }
            rightContent={rightContent}
            hideMeta={hideMeta}
        />
    );
}
